import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectSelectedProduct } from '../../store/selectors';
import { showNotification } from '../../store/slices/notificationSlice';
import { ApiManager } from '../../services/ApiManager';
import { CompleteScrollCollectRequest } from '../../services/types/api';
import parentFrameMessaging from '../../services/ParentFrameMessaging';
import { useSession } from '../../context/SessionContext';
import { useCartMessaging } from '../useCartMessaging';

export const useGameCompletion = (timeRemaining: number, walletAmount: number) => {
  const dispatch = useAppDispatch();
  const { sessionId } = useSession();
  const { cartId } = useCartMessaging();
  const currentSelectedProduct = useAppSelector(selectSelectedProduct);
  const selectedVariantId = useAppSelector((state) => state.game.selectedVariantId);
  const [completionAttempted, setCompletionAttempted] = useState(false);

  useEffect(() => {
    const completeActivity = async () => {
      if (!cartId || !currentSelectedProduct || completionAttempted) {
        return;
      }

      setCompletionAttempted(true);

      try {
        const extractProductId = (id: string): number => {
          if (id.includes('gid://shopify/Product/')) {
            const numericId = id.split('/').pop();
            return parseInt(numericId || id, 10);
          }
          return parseInt(id, 10);
        };

        try {
          if (selectedVariantId && currentSelectedProduct) {
            const productId = extractProductId(currentSelectedProduct.id);
            const variantId = parseInt(selectedVariantId, 10);

            const selectedVariant = currentSelectedProduct.variants?.edges?.find(
              (edge) => edge.node.id === selectedVariantId || parseInt(edge.node.id, 10) === variantId
            )?.node;

            if (selectedVariant) {
              let elapsed = 0;
              try {
                const sessionStartTime = sessionStorage.getItem('playmerce_session_start_time');
                if (sessionStartTime) {
                  const startTimestamp = parseInt(sessionStartTime, 10);
                  elapsed = Math.floor((Date.now() - startTimestamp) / 1000);
                }
              } catch (error) {
                console.warn('Failed to calculate elapsed time:', error);
              }

              await ApiManager.analytics.playmerce_cart_add(
                {
                  time_limit: timeRemaining,
                  elapsed: elapsed,
                  item_discount: walletAmount,
                  product_id: productId,
                  product_title: currentSelectedProduct.title,
                  variant_id: variantId,
                  variant_title: selectedVariant.title,
                  price: {
                    amount: parseFloat(selectedVariant.price),
                    currency: 'EUR',
                  },
                },
                sessionId || undefined
              );

              try {
                localStorage.setItem('playmerce_game_complete', 'true');
              } catch (error) {
                console.warn('Failed to store game complete status:', error);
              }
            }
          }
        } catch (error) {
          console.error('Failed to send cart add analytics:', error);
        }

        const completeRequest: CompleteScrollCollectRequest = {
          product_ids: [extractProductId(currentSelectedProduct.id)],
          cart_id: cartId,
          experiments: {},
          session_id: sessionId || undefined,
        };

        await ApiManager.customer.completeScrollCollect(completeRequest, sessionId || undefined);

        parentFrameMessaging.sendRedirectToCart();
        try {
          const mobileScreenElement = window.parent.document.querySelector('.mobile-screen') as HTMLElement;
          if (mobileScreenElement) {
            mobileScreenElement.style.backgroundImage = "url('img/bg2.png')";
          }
        } catch (error) {
          console.error('Error changing background image:', error);
        }
      } catch (error) {
        console.error('Failed to complete activity:', error);

        dispatch(
          showNotification({
            message: 'Failed to apply discount. Please try again.',
            yPosition: 60,
          })
        );
      }
    };

    if (cartId && currentSelectedProduct && !completionAttempted) {
      completeActivity();
    }
  }, [
    cartId,
    currentSelectedProduct,
    completionAttempted,
    dispatch,
    sessionId,
    selectedVariantId,
    timeRemaining,
    walletAmount,
  ]);
};
